import { computed, type ExtractPropTypes, type PropType } from 'vue';

import { gapList, type GapType, type GapBreakpointType } from '../../typings/gapType';
import { BreakpointDepended } from '../breakpointDepended';

export function withGapProps(defaultValue?: GapType | GapBreakpointType) {
  return {
    /**
     * Odstępy pomiędzy elementami zgodny z `w-spacers`
     */
    gap: {
      type: [Number, Object] as PropType<GapType | GapBreakpointType>,
      default: defaultValue
    },

    /**
     * odstępy pomiędzy elementami w osiX zgodny z `w-spacers`
     */
    gapX: {
      type: [Number, Object] as PropType<GapType | GapBreakpointType>,
      default: defaultValue
    },

    /**
     * odstępy pomiędzy elementami w osiY zgodny z `w-spacers`
     */
    gapY: {
      type: [Number, Object] as PropType<GapType | GapBreakpointType>,
      default: defaultValue
    }
  };
}

/**
 * Zwraca klasy przestrzeni pomiędzy elementami w obu osiach
 *
 * @export
 * @param {string} prefix
 * @param {ExtractPropTypes<typeof withSizeProps>} props
 * @return {*}  {Record<string, boolean>}
 */
export const useGapProps = (prefix: string, props: ExtractPropTypes<ReturnType<typeof withGapProps>>) => {
  return computed(() => {
    if (!props.gap) {
      return;
    }
    if (typeof props.gap === 'number') {
      return `${prefix}g-${props.gap}`;
    }
    if (BreakpointDepended(props.gap).value) {
      return `${prefix}g-${BreakpointDepended(props.gap).value}`;
    }
  });
};

/**
 * Zwraca klasy przestrzeni pomiędzy elementami w osi X
 *
 * @export
 * @param {string} prefix
 * @param {ExtractPropTypes<typeof withSizeProps>} props
 * @return {*}  {Record<string, boolean>}
 */
export const useGapXProps = (prefix: string, props: ExtractPropTypes<ReturnType<typeof withGapProps>>) => {
  return computed(() => {
    if (!props.gapX) {
      return;
    }
    if (typeof props.gapX === 'number') {
      return `${prefix}gx-${props.gapX}`;
    }
    if (BreakpointDepended(props.gapX).value) {
      return `${prefix}gx-${BreakpointDepended(props.gapX).value}`;
    }
  });
};

/**
 * Zwraca klasy przestrzeni pomiędzy elementami w osi Y
 *
 * @export
 * @param {string} prefix
 * @param {ExtractPropTypes<typeof withSizeProps>} props
 * @return {*}  {Record<string, boolean>}
 */
export const useGapYProps = (prefix: string, props: ExtractPropTypes<ReturnType<typeof withGapProps>>) => {
  return computed(() => {
    if (!props.gapY) {
      return;
    }
    if (typeof props.gapY === 'number') {
      return `${prefix}gy-${props.gapY}`;
    }
    if (BreakpointDepended(props.gapY).value) {
      return `${prefix}gy-${BreakpointDepended(props.gapY).value}`;
    }
  });
};

/**
 * Konfiguracja dla storybooka
 */
export const storiesGapArgs = {
  args: {
    gap: undefined,
    gapX: undefined,
    gapY: undefined
  },

  argTypes: {
    gap: {
      control: { type: 'range', min: gapList[0], max: gapList[gapList.length - 1], step: 1 }
    },
    gapX: {
      control: { type: 'range', min: gapList[0], max: gapList[gapList.length - 1], step: 1 }
    },
    gapY: {
      control: { type: 'range', min: gapList[0], max: gapList[gapList.length - 1], step: 1 }
    }
  }
};
